import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Venue } from "./VenueService";
import { DisplayLayout } from "./DisplayLayoutService";
import { ContentBlock } from "./ContentBlockService";
import {
  DisplayRunningMessage,
  DisplayRunningMessageInput,
} from "./DisplayRunningMessageService";
import { DisplayContentAssignmentInput } from "./DisplayContentAssignmentService";
import { Theme } from "./ThemeService";
import { Room } from "./RoomService";
import { DisplayZone, DisplayZoneInput } from "./DisplayZoneService";

// GraphQL queries
const DISPLAY_LIST_QUERY = gql`
  query DisplayList($filter: DisplayFilter, $params: PaginationAndSorting) {
    displayList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        layoutId
        themeId
        name
        description
        emergencyContentBlockId
        blockTransitionAnimation
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
        displayZones {
          displayZoneId
          displayZoneType
          displayZoneTitle
          playListId
          fallbackPlayListId
          scheduleType
          venueId
          roomId
          roomTypeId
          floor
          roomTag
          eventTag
          roomCategoryId
          eventCategoryId
          showPastHours
          showUpcomingHours
        }
        displayRunningMessages {
          runningMessageId
          startDatetime
          endDatetime
          orderNo
        }
        emergencyContentBlock {
          id
          name
          contentType
        }
        theme {
          name
        }
        layout {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_GET_QUERY = gql`
  query DisplayGet($id: ID) {
    displayGet(id: $id) {
      code
      data {
        id
        venueId
        layoutId
        themeId
        name
        description
        emergencyContentBlockId
        blockTransitionAnimation
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
        displayZones {
          displayZoneId
          displayZoneType
          displayZoneTitle
          playListId
          fallbackPlayListId
          scheduleType
          venueId
          roomId
          roomTypeId
          floor
          roomTag
          eventTag
          roomCategoryId
          eventCategoryId
          showPastHours
          showUpcomingHours
        }
        displayRunningMessages {
          runningMessageId
          startDatetime
          endDatetime
          orderNo
        }
        emergencyContentBlock {
          id
          name
          contentType
        }
        theme {
          name
        }
        layout {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_GET_MANY_QUERY = gql`
  query DisplayGetMany($ids: [ID]) {
    displayGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        layoutId
        themeId
        name
        description
        runningMessageId
        emergencyContentBlockId
        blockTransitionAnimation
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
        displayZones {
          displayZoneId
          displayZoneType
          displayZoneTitle
          playListId
          fallbackPlayListId
          scheduleType
          venueId
          roomId
          roomTypeId
          floor
          roomTag
          eventTag
          roomCategoryId
          eventCategoryId
          showPastHours
          showUpcomingHours
        }
        displayRunningMessages {
          runningMessageId
          startDatetime
          endDatetime
          orderNo
        }
        emergencyContentBlock {
          id
          name
          contentType
        }
        theme {
          name
        }
        layout {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const DISPLAY_CREATE_MUTATION = gql`
  mutation DisplayCreate($params: DisplayInput) {
    displayCreate(params: $params) {
      code
      data {
        id
        venueId
        layoutId
        themeId
        name
        description
        emergencyContentBlockId
        blockTransitionAnimation
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
        displayZones {
          displayZoneId
          displayZoneType
          displayZoneTitle
          playListId
          fallbackPlayListId
          scheduleType
          venueId
          roomId
          roomTypeId
          floor
          roomTag
          eventTag
          roomCategoryId
          eventCategoryId
          showPastHours
          showUpcomingHours
        }
        displayRunningMessages {
          runningMessageId
          startDatetime
          endDatetime
          orderNo
        }
        emergencyContentBlock {
          id
          name
          contentType
        }
        theme {
          name
        }
        layout {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_UPDATE_MUTATION = gql`
  mutation DisplayUpdate($where: DisplayWhereInput, $params: DisplayInput) {
    displayUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        layoutId
        themeId
        name
        description
        emergencyContentBlockId
        blockTransitionAnimation
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
        displayZones {
          displayZoneId
          displayZoneType
          displayZoneTitle
          playListId
          fallbackPlayListId
          scheduleType
          venueId
          roomId
          roomTypeId
          floor
          roomTag
          eventTag
          roomCategoryId
          eventCategoryId
          showPastHours
          showUpcomingHours
        }
        displayRunningMessages {
          runningMessageId
          startDatetime
          endDatetime
          orderNo
        }
        emergencyContentBlock {
          id
          name
          contentType
        }
        theme {
          name
        }
        layout {
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_REMOVE_MUTATION = gql`
  mutation DisplayRemove($where: DisplayWhereInput) {
    displayRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_REMOVE_MANY_MUTATION = gql`
  mutation DisplayRemoveMany($where: [DisplayWhereInput]) {
    displayRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface Display {
  id: string;
  venueId?: string;
  layoutId?: string;
  themeId?: string;
  name?: string;
  description?: string | null;
  emergencyContentBlockId?: string | null;
  blockTransitionAnimation?: string | null;
  status?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  userCreated?: User;
  userUpdated?: User;
  venue?: Venue;
  room?: Room;
  layout?: DisplayLayout;
  displayRunningMessages?: DisplayRunningMessage[];
  emergencyContentBlock?: ContentBlock;
  theme?: Theme | null;
  themeConfig?: any;
  layoutConfig?: any;
  displayZones?: DisplayZone[];
}

export interface DisplayFilter {
  id?: string[] | null | undefined;
  venueId?: string[] | null | undefined;
  layoutId?: string[] | null | undefined;
  themeId?: string[] | null | undefined;
  emergencyContentBlockId?: string[] | null | undefined;
  name?: string | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface DisplayInput {
  venueId?: string | null | undefined;
  layoutId?: string | undefined | null;
  themeId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  displayType?: string | null | undefined;
  roomId?: string | null | undefined;
  roomTypeId?: string | null | undefined;
  floor?: string | null | undefined;
  tag?: string | null | undefined;
  runningMessageId?: string | null | undefined;
  emergencyContentBlockId?: string | null | undefined;
  blockTransitionAnimation?: string | null | undefined;
  status?: number | null | undefined;
  contentBlocks?: string[] | null | undefined;
  displayContentAssignments?:
    DisplayContentAssignmentInput[] | null | undefined;
  displayRunningMessages?: DisplayRunningMessageInput[] | null | undefined;
  displayZones?: DisplayZoneInput[] | null | undefined;
}

export interface DisplayWhereInput {
  id: string;
}

/**
 * Display service that handles all display-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayService {
  /**
   * Get a list of displays with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single display by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple displays by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new display
   */
  static async create(params: DisplayInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(DISPLAY_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing display
   */
  static async update(id: string, params: DisplayInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(DISPLAY_UPDATE_MUTATION, {
      where: { id },
      params,
    });
    return result;
  }

  /**
   * Remove/delete a display
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(DISPLAY_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple displays
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      DISPLAY_REMOVE_MANY_MUTATION,
      {
        where,
      },
    );
    return result;
  }
}
